import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Scissors, CheckCircle2, Undo2, X, Clapperboard } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "./StatusBadge";
import { TrimPanel } from "./TrimPanel";
import { useJoins, JoinsSummary, JoinMarker, InnerMarker, markJoin } from "./JoinCheck";

const clock = (s) => { s = Math.max(0, Math.floor(s)); return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };
const len = (s) => s.duration || 20;

const Timeline = ({ list, curIdx, global, total, onSeek, children }) => (
  <div className="relative pt-2">
  {children}
  <div className="relative flex h-7 rounded overflow-hidden border border-[#2A3245] bg-[#0B0D14]" data-testid="preview-timeline">
    {list.map((s, i) => (
      <button key={s.id} data-testid={`preview-seg-${s.number}`} title={`Scene ${s.number} · ${len(s).toFixed(1)}s · ${s.status}`}
        style={{ width: `${(len(s) / total) * 100}%` }}
        onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); onSeek(i, ((e.clientX - r.left) / r.width) * len(s)); }}
        className={`h-full border-r border-[#0B0D14] font-mono text-[9px] text-left px-1 truncate transition-colors ${s.status === "approved" ? "bg-amber-500/25 text-amber-200" : "bg-slate-600/25 text-slate-300"} ${i === curIdx ? "ring-1 ring-inset ring-amber-400" : "hover:bg-white/10"}`}>
        {s.number}
      </button>
    ))}
    <div className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none" style={{ left: `${(global / total) * 100}%` }} data-testid="preview-playhead" />
  </div>
  </div>
);

export const EpisodePreview = ({ ep, scenes, reload, onClose, onOpenScene }) => {
  const [onlyApproved, setOnlyApproved] = useState(false);
  const [curId, setCurId] = useState(null);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [trimming, setTrimming] = useState(false);
  const [trimInit, setTrimInit] = useState(null);
  const v = useRef(null);
  const pending = useRef(null);
  const list = useMemo(() => scenes.filter((s) => s.video_path && (!onlyApproved || s.status === "approved")), [scenes, onlyApproved]);
  const found = list.findIndex((s) => s.id === curId);
  const idx = found < 0 ? 0 : found;
  const s = list[idx];
  const total = list.reduce((n, x) => n + len(x), 0) || 1;
  const joins = useJoins(ep.id, onlyApproved, list.map((x) => x.video_path).join(","));
  const offsetOf = (id) => list.slice(0, list.findIndex((x) => x.id === id)).reduce((n, x) => n + len(x), 0);
  const global = list.slice(0, idx).reduce((n, x) => n + len(x), 0) + t;

  useEffect(() => { if (s && s.id !== curId) setCurId(s.id); }, [s, curId]);
  useEffect(() => { if (!pending.current) setT(0); }, [s?.video_path]);

  const go = (i, at = 0, autoplay = playing) => {
    if (i < 0 || i >= list.length) return;
    pending.current = { at, autoplay };
    setT(at);
    if (list[i].id === s?.id) { v.current.currentTime = at; if (autoplay) v.current.play(); pending.current = null; }
    else setCurId(list[i].id);
  };
  const loaded = () => {
    const p = pending.current;
    if (!p) return;
    v.current.currentTime = p.at;
    if (p.autoplay) v.current.play().catch(() => {});
    pending.current = null;
  };
  const toggle = () => v.current && (v.current.paused ? v.current.play() : v.current.pause());
  const ended = () => (idx < list.length - 1 ? go(idx + 1, 0, true) : setPlaying(false));
  const approve = async (un) => {
    try { await http.post(`/scenes/${s.id}/${un ? "unapprove" : "approve"}`); toast.success(un ? `Scene ${s.number} unapproved` : `Scene ${s.number} approved`); await reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const startTrim = (id = s.id, init = null) => { v.current?.pause(); setTrimInit(init); if (id !== s.id) setCurId(id); setTrimming(true); };
  const playJoin = (j) => { const i = list.findIndex((x) => x.id === j.a_id); go(i, Math.max(0, len(list[i]) - 2), true); };
  const retake = (j) => {
    onOpenScene(j.b_id);
    toast(j.a_approved
      ? `Scene ${j.b_number} is open in Studio — press "Keep & try another" to film a take that starts from scene ${j.a_number}'s last frame.`
      : `Scene ${j.b_number} is open in Studio. Approve scene ${j.a_number} first so the new take starts from its last frame, then press "Keep & try another".`, { duration: 8000 });
  };

  if (!list.length) {
    return (
      <div className="panel p-6 space-y-3" data-testid="episode-preview">
        <div className="flex items-center gap-2"><Clapperboard className="w-4 h-4 text-amber-400" /><span className="font-medium">Episode preview</span>
          <Button data-testid="close-episode-preview-btn" size="sm" variant="ghost" onClick={onClose} className="ml-auto rounded-full"><X className="w-4 h-4" /></Button></div>
        <div className="text-sm text-slate-400" data-testid="preview-empty">{onlyApproved ? "No approved scenes yet." : "No scene has a clip yet — generate some scenes first."}</div>
        {onlyApproved && <Button size="sm" variant="secondary" onClick={() => setOnlyApproved(false)} className="rounded-full">Show all clips</Button>}
      </div>
    );
  }

  return (
    <div className="panel p-4 md:p-5 space-y-4" data-testid="episode-preview">
      <div className="flex flex-wrap items-center gap-3">
        <Clapperboard className="w-4 h-4 text-amber-400" />
        <span className="font-medium">Episode preview</span>
        <span className="font-mono text-[11px] text-slate-500">{list.length} clips · {clock(total)}</span>
        <JoinsSummary pending={joins.pending} joins={joins.joins} inner={(joins.inner || []).filter((j) => list.some((x) => x.id === j.scene_id))} />
        <label className="flex items-center gap-2 text-xs text-slate-400 ml-auto cursor-pointer">
          <Switch data-testid="preview-only-approved-switch" checked={onlyApproved} onCheckedChange={(c) => { setOnlyApproved(c); setT(0); }} />Approved only
        </label>
        <Button data-testid="close-episode-preview-btn" size="sm" variant="ghost" onClick={onClose} className="rounded-full"><X className="w-4 h-4" /></Button>
      </div>

      {trimming ? (
        <TrimPanel key={`${s.id}-${JSON.stringify(trimInit)}`} s={s} aspect={ep.aspect_ratio} init={trimInit} onClose={() => { setTrimming(false); setTrimInit(null); }} reload={reload} />
      ) : (
        <div className={`relative bg-black rounded-lg overflow-hidden border border-[#2A3245] mx-auto ${ep.aspect_ratio === "9:16" ? "aspect-[9/16] max-h-[60vh]" : "aspect-video w-full max-w-[calc(60vh*16/9)]"}`}>
          <video ref={v} key={s.video_path} data-testid="preview-episode-video" src={fileUrl(s.video_path)} poster={fileUrl(s.thumb_path)} playsInline controls
            onLoadedMetadata={loaded} onTimeUpdate={(e) => setT(e.currentTarget.currentTime)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={ended}
            onClick={(e) => { e.preventDefault(); toggle(); }} className="w-full h-full object-contain" />
          <div className="absolute top-2 left-2 font-mono text-[10px] bg-black/70 px-2 py-1 rounded" data-testid="preview-now-playing">
            SCENE {String(s.number).padStart(3, "0")} · {idx + 1} OF {list.length}
          </div>
          {list[idx + 1] && <video src={fileUrl(list[idx + 1].video_path)} preload="auto" muted className="hidden" />}
        </div>
      )}

      <Timeline list={list} curIdx={idx} global={global} total={total} onSeek={(i, at) => !trimming && go(i, at)}>
        {joins.joins.map((j) => (
          <JoinMarker key={`${j.a_id}-${j.b_id}`} j={j} left={(offsetOf(j.b_id) / total) * 100}
            onPlay={() => !trimming && playJoin(j)} onTrimStart={() => startTrim(j.b_id, { start: j.start_cut || 1 })}
            onTrimEnd={() => startTrim(j.a_id, { end: j.end_cut })} onRetake={() => retake(j)} onMark={(ok) => markJoin(ep.id, j, ok, joins.reload)} />
        ))}
        {(joins.inner || []).filter((j) => list.some((x) => x.id === j.scene_id)).map((j) => (
          <InnerMarker key={`in-${j.scene_id}`} j={j} left={((offsetOf(j.scene_id) + j.t) / total) * 100}
            onPlay={() => { if (!trimming) go(list.findIndex((x) => x.id === j.scene_id), Math.max(0, j.t - 2), true); }}
            onCut={() => startTrim(j.scene_id, { cut: j.cut })} onFirst={() => startTrim(j.scene_id, { end: j.first_end })}
            onSecond={() => startTrim(j.scene_id, { start: j.second_start })}
            onRetake={() => { onOpenScene(j.scene_id); toast(`Scene ${j.number} is open in Studio — press "Keep & try another" for a new take.`); }}
            onMark={(ok) => markJoin(ep.id, j, ok, joins.reload)} />
        ))}
      </Timeline>

      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="preview-prev-btn" size="sm" variant="secondary" disabled={idx === 0 || trimming} onClick={() => go(idx - 1)} className="rounded-full" title="Previous scene"><SkipBack className="w-4 h-4" /></Button>
        <Button data-testid="preview-play-btn" size="sm" disabled={trimming} onClick={toggle} className="rounded-full w-20" title="Play / pause (or click the video)">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </Button>
        <Button data-testid="preview-next-btn" size="sm" variant="secondary" disabled={idx === list.length - 1 || trimming} onClick={() => go(idx + 1)} className="rounded-full" title="Next scene"><SkipForward className="w-4 h-4" /></Button>
        <span className="font-mono text-xs text-slate-400" data-testid="preview-time">{clock(global)} / {clock(total)}</span>
        <StatusBadge status={s.status} testId="preview-scene-status" />
        <div className="ml-auto flex flex-wrap gap-2">
          {!trimming && (
            <Button data-testid="preview-trim-btn" size="sm" variant="secondary" disabled={s.status === "generating"} onClick={() => startTrim()} className="rounded-full">
              <Scissors className="w-3.5 h-3.5 mr-1.5" />Trim scene {s.number}
            </Button>
          )}
          {s.status === "approved" ? (
            <Button data-testid="preview-unapprove-btn" size="sm" variant="secondary" onClick={() => approve(true)} className="rounded-full"><Undo2 className="w-3.5 h-3.5 mr-1.5" />Unapprove</Button>
          ) : (
            <Button data-testid="preview-approve-btn" size="sm" disabled={s.status === "generating"} onClick={() => approve(false)} className="rounded-full bg-amber-500 text-black hover:bg-amber-400">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Approve scene {s.number}
            </Button>
          )}
        </div>
      </div>
      {s.status === "generating" && <div className="text-[11px] text-violet-300" data-testid="preview-scene-busy">{s.progress || "Working"}… this scene will update when done.</div>}
      {s.error?.startsWith("Trim failed") && <div className="text-[11px] text-red-400" data-testid="preview-scene-error">{s.error}</div>}
    </div>
  );
};
